import LoadingOverlay from '../components/common/LoadingOverlay/LoadingOverlay';
import React, { useEffect } from 'react';
import Sidebar from '../components/common/SideBar';
import { useKeycloak, useAuthSafe } from '../components/ConditionalAuthProvider';
import { isKeycloakEnabled } from '../config/authConfig';
import WelllynxLayout from '../components/layouts/WelllynxLayout';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { Navigate } from 'react-router-dom';

interface ProtectedWrapperProps {
  children: React.ReactNode;
}

const ProtectedWrapper: React.FC<ProtectedWrapperProps> = ({ children }) => {
  // Use safe hooks that work in both modes
  const keycloakAuth = useKeycloak();
  const backendAuth = useAuthSafe();
  const shouldTriggerKeycloakLogin =
    isKeycloakEnabled() && keycloakAuth.initialized && !keycloakAuth.keycloak?.authenticated;

  useEffect(() => {
    if (!shouldTriggerKeycloakLogin) {
      return;
    }
    void keycloakAuth.keycloak?.login?.();
  }, [keycloakAuth.keycloak, shouldTriggerKeycloakLogin]);

  if (isKeycloakEnabled()) {
    // Keycloak mode - use existing Keycloak authentication
    const { keycloak, initialized } = keycloakAuth;

    if (!initialized) {
      return <LoadingOverlay visible />;
    }

    if (!keycloak?.authenticated) {
      return <LoadingOverlay visible />;
    }

    return FEATURE_FLAGS.welllynxLayout ? (
      <WelllynxLayout>{children}</WelllynxLayout>
    ) : (
      <>
        <Sidebar />
        {children}
      </>
    );
  } else {
    // Non-Keycloak mode - use backend authentication
    const { authenticated, initialized } = backendAuth;

    if (!initialized) {
      return <LoadingOverlay visible />;
    }

    if (!authenticated) {
      // Force return to login route when backend JWT is invalid/expired
      return <Navigate to="/login" replace />;
    }

    return FEATURE_FLAGS.welllynxLayout ? (
      <WelllynxLayout>{children}</WelllynxLayout>
    ) : (
      <>
        <Sidebar />
        {children}
      </>
    );
  }
};

export default ProtectedWrapper;
